import { z } from 'zod';
/**
 * Agent collection (DEV-16821) — a named group of agents, sugar over bindings.
 * A memory or skill bound to a collection resolves to EVERY member agent, so
 * "apply this to all triage agents (current + future)" needs no per-agent
 * re-binding. Collections resolve to bindings at read time; the isolation
 * invariant is unchanged — an agent only ever reaches collections it belongs to,
 * inside the outer org boundary. Lives in the main Cortex DB (unified store).
 */
/** Max collection name length (enforced by the create/rename DTO via Zod). */
export const collectionNameMax = 200;
// ─── Entity ───────────────────────────────────────────────────────────────────
export const AgentCollectionSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
/**
 * A collection with its resolved membership + applicability sets — what the
 * detail endpoint returns. `agentIds` are the member agents; `memoryIds` /
 * `skillIds` are the memories/skills bound to the collection.
 */
export const AgentCollectionDetailSchema = AgentCollectionSchema.extend({
    agentIds: z.array(z.string()),
    memoryIds: z.array(z.string()),
    skillIds: z.array(z.string()),
});
/**
 * List-view projection — counts instead of the full member/binding id sets, so
 * the list endpoint returns a bounded payload regardless of how many agents /
 * memories / skills a collection carries (the detail endpoint returns the full
 * sets). Right shape for a picker dropdown too.
 */
export const AgentCollectionSummarySchema = AgentCollectionSchema.extend({
    memberCount: z.number().int().nonnegative(),
    memoryCount: z.number().int().nonnegative(),
    skillCount: z.number().int().nonnegative(),
});
// ─── Write inputs ─────────────────────────────────────────────────────────────
export const CreateAgentCollectionInputSchema = z.object({
    name: z.string().min(1).max(collectionNameMax),
    /** Optional initial member agents (each must be global or same-org). */
    agentIds: z.array(z.string()).optional().default([]),
});
/** Rename — the only mutable field on a collection. */
export const UpdateAgentCollectionInputSchema = z.object({
    name: z.string().min(1).max(collectionNameMax),
});
export const AddCollectionMembersInputSchema = z.object({
    agentIds: z.array(z.string()).min(1),
});
export const BindCollectionMemoriesInputSchema = z.object({
    memoryIds: z.array(z.string()).min(1),
});
export const BindCollectionSkillsInputSchema = z.object({
    skillIds: z.array(z.string()).min(1),
});
//# sourceMappingURL=agent-collection.js.map